import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { A11yModule } from '@angular/cdk/a11y';
import { TranslateModule } from '@ngx-translate/core';
import { HelpStateService } from './help-state.service';
import { TourService } from './tour.service';
import { TourOverlayComponent } from './tour-overlay.component';
import { canSeeArticle, canStartTour } from './help-access';
import { searchArticles } from './help-search';
import { HelpArticle, HelpCategory, HelpCategoryId, HelpContentModule, Tour } from './help.models';

type HelpView =
  | { kind: 'home' }
  | { kind: 'category'; id: HelpCategoryId }
  | { kind: 'article'; id: string };

export type TourStatus = 'new' | 'paused' | 'done';

@Component({
  selector: 'app-help-center',
  standalone: true,
  imports: [A11yModule, TranslateModule, TourOverlayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './help-center.component.html',
  styleUrl: './help-center.component.css',
})
export class HelpCenterComponent {
  readonly state = inject(HelpStateService);
  private tour = inject(TourService);
  private router = inject(Router);

  readonly lang = this.state.lang;
  readonly prefs = this.state.prefs;
  readonly panelOpen = this.state.panelOpen;

  readonly content = signal<HelpContentModule | null>(null);
  readonly loadError = signal(false);
  readonly query = signal('');
  readonly view = signal<HelpView>({ kind: 'home' });

  private launcher = viewChild<ElementRef<HTMLButtonElement>>('launcher');
  private searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  readonly pulse = computed(() => this.prefs().launcherOpens < 2);

  readonly articles = computed<HelpArticle[]>(() => {
    const content = this.content();
    const ctx = this.state.context();
    return content ? content.HELP_ARTICLES.filter(a => canSeeArticle(a, ctx)) : [];
  });

  readonly tours = computed<Tour[]>(() => {
    const content = this.content();
    const ctx = this.state.context();
    return content ? content.TOURS.filter(t => canStartTour(t, ctx)) : [];
  });

  readonly categories = computed<HelpCategory[]>(() => {
    const content = this.content();
    if (!content) return [];
    const used = new Set(this.articles().map(a => a.category));
    return content.HELP_CATEGORIES.filter(c => used.has(c.id));
  });

  readonly results = computed(() => searchArticles(this.articles(), this.query(), this.lang()));
  readonly searching = computed(() => this.query().trim().length > 0);

  readonly currentCategory = computed(() => {
    const v = this.view();
    if (v.kind !== 'category') return null;
    return this.categories().find(c => c.id === v.id) ?? null;
  });

  readonly categoryArticles = computed(() => {
    const category = this.currentCategory();
    return category ? this.articles().filter(a => a.category === category.id) : [];
  });

  readonly currentArticle = computed(() => {
    const v = this.view();
    if (v.kind !== 'article') return null;
    return this.articles().find(a => a.id === v.id) ?? null;
  });

  readonly articleTour = computed(() => {
    const article = this.currentArticle();
    if (!article?.tourId) return null;
    return this.tours().find(t => t.id === article.tourId) ?? null;
  });

  readonly relatedArticles = computed(() => {
    const article = this.currentArticle();
    if (!article) return [];
    return this.articles().filter(a => a.category === article.category && a.id !== article.id);
  });
  tourStatus(tour: Tour): TourStatus {
    const progress = this.prefs().tours[tour.id];
    if (!progress) return 'new';
    return progress.done ? 'done' : 'paused';
  }

  categoryLabel(id: HelpCategoryId): string {
    const category = this.content()?.HELP_CATEGORIES.find(c => c.id === id);
    return category ? category.label[this.lang()] : '';
  }

  async open(): Promise<void> {
    if (this.tour.active()) this.tour.pause();
    this.state.updatePrefs(prefs => ({
      ...prefs,
      launcherMinimized: false,
      launcherOpens: Math.min(prefs.launcherOpens + 1, 1000),
    }));
    this.panelOpen.set(true);
    setTimeout(() => this.searchInput()?.nativeElement.focus({ preventScroll: true }));
    await this.ensureContent();
  }

  close(restoreFocus = true): void {
    this.panelOpen.set(false);
    if (restoreFocus) setTimeout(() => this.launcher()?.nativeElement.focus({ preventScroll: true }));
  }

  minimize(): void {
    this.state.updatePrefs(prefs => ({ ...prefs, launcherMinimized: true }));
  }

  async ensureContent(): Promise<void> {
    if (this.content()) return;
    this.loadError.set(false);
    try {
      this.content.set(await this.state.loadContent());
    } catch {
      this.loadError.set(true);
    }
  }

  onQuery(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }

  clearQuery(): void {
    this.query.set('');
    this.searchInput()?.nativeElement.focus();
  }

  openCategory(id: HelpCategoryId): void {
    this.query.set('');
    this.view.set({ kind: 'category', id });
  }

  openArticle(id: string): void {
    this.query.set('');
    this.view.set({ kind: 'article', id });
  }

  back(): void {
    const v = this.view();
    const article = this.currentArticle();
    if (v.kind === 'article' && article) {
      this.view.set({ kind: 'category', id: article.category });
    } else {
      this.view.set({ kind: 'home' });
    }
  }

  goTo(route: string): void {
    this.close(false);
    void this.router.navigateByUrl(route);
  }

  startTour(tourId: string, restart: boolean): void {
    this.close(false);
    void this.tour.start(tourId, { restart });
  }
}
